import { getAnalytics, logEvent } from 'firebase/analytics';

const STORAGE_KEY = 'podeux_onboarding_funnel_v1';
const LIFETIME_MS = 24 * 60 * 60 * 1000;
const MILESTONES = new Set([
    'onboarding_home_view', 'onboarding_picker_view', 'onboarding_pick',
    'onboarding_sample_ready', 'onboarding_play_start', 'onboarding_sample_complete',
    'onboarding_verified', 'onboarding_ready',
]);
const EVENTS = new Set([...MILESTONES, 'onboarding_generate_start', 'onboarding_signup_open',
    'onboarding_sms_request', 'onboarding_sms_sent', 'onboarding_sms_failed',
    'onboarding_verify_start', 'onboarding_verify_failed', 'onboarding_error']);
const STAGES = new Set(['picker', 'generate', 'playback', 'sms', 'verify', 'bootstrap']);
const TRIGGERS = new Set(['sample_complete', 'skip', 'save', 'listen', 'context', 'other']);
const SAFE_CODES = new Set([
    'auth/invalid-phone-number', 'auth/missing-phone-number', 'auth/invalid-verification-code',
    'auth/missing-verification-code', 'auth/code-expired', 'auth/session-expired',
    'auth/network-request-failed', 'auth/too-many-requests', 'auth/quota-exceeded',
    'auth/captcha-check-failed', 'auth/invalid-app-credential', 'auth/invalid-credential',
    'auth/credential-already-in-use', 'auth/account-exists-with-different-credential',
    'starter_roster_stale', 'roster_stale', 'starter_invalid_selection',
    'starter_highlights_unavailable', 'empty_roster', 'empty_sample',
    'network_error', 'http_error', 'media_error', 'play_rejected', 'unknown',
]);
let memoryFlow = null;
const FLOW_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const newFlowId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    const bytes = window.crypto?.getRandomValues?.(new Uint8Array(16))
        || Uint8Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const persist = flow => {
    memoryFlow = flow;
    try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(flow)); } catch { /* Tab memory fallback. */ }
    return flow;
};

export const readOnboardingFlow = () => {
    let flow;
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        try { flow = raw ? JSON.parse(raw) : null; } catch { return null; }
    } catch { flow = memoryFlow; }
    const age = Date.now() - flow?.createdAt;
    return FLOW_ID.test(flow?.flowId) && Number.isFinite(flow?.createdAt) && age >= 0 && age < LIFETIME_MS
        && Array.isArray(flow.milestones) ? flow : null;
};

export const onboardingErrorCode = error => {
    const code = error?.response?.data?.error || error?.code || error?.error;
    if (SAFE_CODES.has(code)) return code;
    if (error?.response) return 'http_error';
    if (error?.request) return 'network_error';
    return 'unknown';
};

// Only explicit product fields enter Analytics. Firebase errors, credentials,
// phone numbers, user IDs, search text, and API payloads never pass through.
export const trackOnboardingEvent = (name, metadata = {}, { start = false, flowId } = {}) => {
    if (!EVENTS.has(name)) return false;
    let flow = readOnboardingFlow();
    if (start && (!flow || flow.ended)) {
        flow = persist({ flowId: newFlowId(), createdAt: Date.now(), milestones: [], ended: false });
    }
    if (!flow || flow.ended || flowId === null || (flowId && flow.flowId !== flowId)
        || (MILESTONES.has(name) && flow.milestones.includes(name))) return false;
    // Auth observers can finish backend setup before confirm's promise settles.
    // Keep the flow open until the SDK supplies verified and its classification.
    if (name === 'onboarding_ready' && !flow.milestones.includes('onboarding_verified')) {
        persist({ ...flow, pendingBackendReady: true });
        return false;
    }
    const parameters = {
        schema_version: 1, ui_variant: 'current', client_platform: 'web', flow_id: flow.flowId,
    };
    if (['new', 'existing'].includes(metadata.account_type)) parameters.account_type = metadata.account_type;
    else if (name === 'onboarding_ready' && ['new', 'existing'].includes(flow.accountType)) parameters.account_type = flow.accountType;
    if (TRIGGERS.has(metadata.signup_trigger)) parameters.signup_trigger = metadata.signup_trigger;
    if (STAGES.has(metadata.error_stage)) parameters.error_stage = metadata.error_stage;
    if (SAFE_CODES.has(metadata.error_code)) parameters.error_code = metadata.error_code;
    persist({
        ...flow,
        ...(parameters.account_type ? { accountType: parameters.account_type } : {}),
        milestones: MILESTONES.has(name) ? [...flow.milestones, name] : flow.milestones,
        ...(name === 'onboarding_ready' ? { pendingBackendReady: false } : {}),
        ended: name === 'onboarding_ready',
    });
    try { logEvent(getAnalytics(), name, parameters); } catch { /* Analytics never blocks onboarding. */ }
    if (name === 'onboarding_verified' && flow.pendingBackendReady === true) {
        trackOnboardingEvent('onboarding_ready', {}, { flowId: flow.flowId });
    }
    return true;
};

export const trackOnboardingError = (stage, error, options) => trackOnboardingEvent('onboarding_error', {
    error_stage: stage, error_code: onboardingErrorCode(error),
}, options);

export const onboardingSignupTrigger = ({ reason, action } = {}) => {
    if (action === 'next_preview' || reason === 'continue') return 'skip';
    if (reason === 'complete' && !action) return 'sample_complete';
    if (action === 'save' || action === 'bookmark') return 'save';
    if (action === 'full_episode' || action === 'continue_listening') return 'listen';
    if (['topic', 'speaker', 'podcast', 'source', 'summary', 'preferences'].includes(action)) return 'context';
    return 'other';
};

// FirebaseUI calls these supported Auth methods and confirmation.confirm.
// Wrapping the returned promise measures SDK attempts and outcomes, including
// resend and anonymous-account collision recovery, without inspecting inputs.
export const installOnboardingPhoneFunnel = ({ auth, user, onBusyChange } = {}) => {
    const restorers = [];
    const busy = value => { try { onBusyChange?.(value); } catch { /* UI notification cannot change Auth. */ } };
    const install = (target, method, anonymousLink = false) => {
        const original = target?.[method];
        if (typeof original !== 'function') return;
        const wrapped = function (...args) {
            const flowId = readOnboardingFlow()?.flowId || null;
            const options = { flowId };
            busy(true);
            trackOnboardingEvent('onboarding_sms_request', {}, options);
            let request;
            try { request = original.apply(this, args); } catch (error) { request = Promise.reject(error); }
            return Promise.resolve(request).then(confirmation => {
                trackOnboardingEvent('onboarding_sms_sent', {}, options);
                if (typeof confirmation?.confirm !== 'function') return confirmation;
                const confirm = confirmation.confirm.bind(confirmation);
                const result = Object.assign(Object.create(Object.getPrototypeOf(confirmation)), confirmation);
                result.confirm = async (...verificationArgs) => {
                    busy(true);
                    trackOnboardingEvent('onboarding_verify_start', {}, options);
                    try {
                        const credential = await confirm(...verificationArgs);
                        const isNewUser = credential?.additionalUserInfo?.isNewUser;
                        let accountType;
                        if (anonymousLink && user?.uid && credential?.user?.uid) {
                            accountType = credential.user.uid === user.uid ? 'new' : 'existing';
                        } else if (typeof isNewUser === 'boolean') {
                            accountType = isNewUser ? 'new' : 'existing';
                        }
                        trackOnboardingEvent('onboarding_verified', { account_type: accountType }, options);
                        return credential;
                    } catch (error) {
                        trackOnboardingEvent('onboarding_verify_failed', { error_code: onboardingErrorCode(error) }, options);
                        trackOnboardingError('verify', error, options);
                        throw error;
                    } finally { busy(false); }
                };
                return result;
            }, error => {
                trackOnboardingEvent('onboarding_sms_failed', { error_code: onboardingErrorCode(error) }, options);
                trackOnboardingError('sms', error, options);
                throw error;
            }).finally(() => busy(false));
        };
        target[method] = wrapped;
        restorers.push(() => { if (target[method] === wrapped) target[method] = original; });
    };
    install(auth, 'signInWithPhoneNumber');
    if (user?.isAnonymous) install(user, 'linkWithPhoneNumber', true);
    return () => restorers.reverse().forEach(restore => restore());
};
